import {
  MerchantCommissionType,
  MerchantDeliverabilitySupportStatus,
  MerchantsByDeveloperQuery,
} from 'app/graphql/generated/admin/graphql';
import { Avatar, AvatarImage } from 'components/ui/avatar';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Separator } from 'components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { Typography } from 'components/ui/typography';
import { cn } from 'lib/utils';

import { TruckIcon } from 'lucide-react';

import { MerchantInfo } from '../styles';

type MerchantItemProps = MerchantsByDeveloperQuery['merchantsByDeveloper']['merchants'][number];

const DELIVERABILITY_SUPPORT_PROPS: Record<
  MerchantDeliverabilitySupportStatus,
  { className: string; tooltip: string } | undefined
> = {
  [MerchantDeliverabilitySupportStatus.Full]: {
    className: 'text-green-500',
    tooltip: 'All shipping rates offered by this merchant are supported by Rye.',
  },
  [MerchantDeliverabilitySupportStatus.Partial]: {
    className: 'text-yellow-500',
    tooltip: 'Some dynamic shipping rates offered by this merchant might not be supported by Rye.',
  },
  [MerchantDeliverabilitySupportStatus.Unknown]: {
    className: 'text-muted-foreground',
    tooltip: "We're working on figuring out which shipping rates Rye supports for this merchant.",
  },
};

function getFallbackImageDomain(domain: string) {
  if (domain.includes('.myshopify.com')) {
    return 'shopify.com';
  }
  return domain;
}

export function MerchantItem({
  name,
  deliverabilitySupport,
  domain,
  commissionType,
  commissionRate,
  logo,
}: MerchantItemProps) {
  const commissionTypeLabel =
    commissionType === MerchantCommissionType.Discount
      ? 'Discount commissions'
      : 'Standard commissions';

  const deliverabilitySupportProps = DELIVERABILITY_SUPPORT_PROPS[deliverabilitySupport];

  return (
    <Card>
      <CardHeader>
        <MerchantInfo>
          <Avatar className="h-20 w-20" style={{ border: '1px solid #ddd' }}>
            <AvatarImage src={logo} alt={`${name}'s logo`} style={{ objectFit: 'contain' }} />
            <AvatarImage
              src={`https://logo.clearbit.com/${getFallbackImageDomain(domain)}`}
              alt="shopifyLogo"
              style={{
                objectFit: 'contain',
                filter: !domain.includes('.myshopify.com')
                  ? 'sepia(100%) saturate(200%) brightness(70%) hue-rotate(330deg)'
                  : undefined,
              }}
            />
          </Avatar>
          <div className="space-y-1" style={{ width: 'calc(100% - 80px)' }}>
            <Typography variant="h4" as="h4" className="w-full break-words leading-none">
              {name}
            </Typography>
            <Typography variant="small" as="p" className="w-full break-words">
              {domain}
            </Typography>
          </div>
        </MerchantInfo>
      </CardHeader>
      <CardContent>
        <Separator className="my-4" />
        <Typography variant="small" as="div" className="flex h-5 items-center space-x-2">
          <div>{commissionRate}% rate</div>
          <Separator orientation="vertical" />
          <div>{commissionTypeLabel}</div>
          {deliverabilitySupportProps && (
            <>
              <Separator orientation="vertical" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <TruckIcon className={cn('h-4 w-4', deliverabilitySupportProps.className)} />
                </TooltipTrigger>
                <TooltipContent>{deliverabilitySupportProps.tooltip}</TooltipContent>
              </Tooltip>
            </>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
}
